<script setup lang="ts">
import useMobile from '@/composables/useMobileDependable';
import StfRadioChoice from '@/ui/radio-choice/StfRadioChoice.vue';
import { useNotification } from '@/composables/useNotification';
import { RadioItemType } from '@/ui/radio-choice/type';
import { computed, ref, watch } from 'vue';
import { MarkType } from '@/core/interface/api-interface';

const imagesConfig = [
    {
        type: RadioItemType.IMAGE,
        text: 'Nike logo',
        src: '/img/mark-example-nike.png'
    }];

const textConfig = [
    {
        type: RadioItemType.TEXT,
        text: 'NIKE',
    }];

const wordMarkConfig = [
    {
        type: RadioItemType.IMAGE,
        text: 'NIKE',
        src: '/img/mark-example-nike-wordmark.png'
    }];

interface Props {
    modelValue: MarkType | null;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);

const { showInfoNotification } = useNotification();
const { isMobile } = useMobile();
const imageSelected = ref(props.modelValue === MarkType.IMAGE);
const textSelected = ref(props.modelValue === MarkType.WORDS);
const wordMarkSelected = ref(props.modelValue === MarkType.WORDS_IMAGE);

const text = computed(() => {
    if (isMobile.value) {
        return [textConfig[0]]
    }

    return textConfig;
});

const images = computed(() => {
    if (isMobile.value) {
        return [imagesConfig[0]]
    }

    return imagesConfig;
});

watch(imageSelected, (newValue) => {
    if (newValue) {
        showInfoNotification('<b>Protecting the visual design of your logo as a whole -</b> including all colour variations.');
        emit('update:modelValue', MarkType.IMAGE);
    }
})

watch(textSelected, (newValue) => {
    if (newValue) {
        showInfoNotification('<b>Get broadest protection for the words in your brand name.</b> Protecting in word form gives protections to those words in all forms, styles, fonts, and colours.');
        emit('update:modelValue', MarkType.WORDS);
    }
})

watch(wordMarkSelected, (newValue) => {
    if (newValue) {
        showInfoNotification('<b>Protecting the overall look of both your logo and your brand name.</b> If you want some protection for both your logo and your brand name in a single trademark, this option is providing you with the BEST VALUE.');
        emit('update:modelValue', MarkType.WORDS_IMAGE);
    }
})

watch(() => props.modelValue, (newValue) => {
    if (newValue === MarkType.IMAGE) {
        imageSelected.value = true;
        textSelected.value = false;
        wordMarkSelected.value = false;
        return;
    }

    if (newValue === MarkType.WORDS) {
        textSelected.value = true;
        imageSelected.value = false;
        wordMarkSelected.value = false;
        return;
    }

    if (newValue === MarkType.WORDS_IMAGE) {
        wordMarkSelected.value = true;
        imageSelected.value = false;
        textSelected.value = false;
        return;
    }

    textSelected.value = false;
    imageSelected.value = false;
    wordMarkSelected.value = false;
})
</script>

<template>
    <StfRow class="mt-2">
        <StfCol col="12" colDesktop="auto">
            <StfRadioChoice v-model="textSelected" title="Word" subtitle="A brand name, word/s or slogan"
                            size="large" :items="text" color="primary" inverted></StfRadioChoice>
        </StfCol>
        <StfCol col="12" colDesktop="auto">
            <StfRadioChoice v-model="imageSelected" title="Logo" subtitle="An image or logo, without any words" size="large"
                            :items="images" color="primary" inverted></StfRadioChoice>
        </StfCol>
        <StfCol col="12" colDesktop="auto">
            <StfRadioChoice v-model="wordMarkSelected" title="Logo with words" subtitle="An image or logo, with words"
                            size="large" :items="wordMarkConfig" color="primary" inverted></StfRadioChoice>
        </StfCol>
    </StfRow>
</template>

<style scoped></style>
